/**
 * React Query hooks for the binary on/off duty status.
 *
 * SMS analog of AIM's `useDutyStatus` (`apps/aegix-aim-react/src/hooks/
 * useDutyData.ts`) — react-query only, no redux. SMS has no navbar duty
 * indicator, so the fetched state drives the profile toggle directly; the
 * write hook invalidates the duty query so the toggle reflects the server.
 *
 * `userId` is the user's `user_duty_id` (the duty row pk), gated to `null`
 * by the caller when the user lacks the duty permissions so the query stays
 * disabled.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUserDuty,
  updateUserDuty,
  type UserDuty,
} from '../../services/duty';

export const dutyQueryKeys = {
  all: ['duty'] as const,
  status: (userId: number) => [...dutyQueryKeys.all, 'status', userId] as const,
};

export function useDutyStatus(userId: number | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: dutyQueryKeys.status(userId ?? 0),
    queryFn: () => getUserDuty(userId as number),
    enabled: !!userId,
    staleTime: 30000,
  });

  const mutation = useMutation({
    mutationFn: (payload: {
      is_on_duty: boolean;
      scheduled_at?: string | null;
    }) => updateUserDuty(userId as number, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dutyQueryKeys.all });
    },
  });

  return {
    dutyStatus: query.data as UserDuty | undefined,
    isLoading: query.isLoading,
    updateDuty: mutation,
    isUpdating: mutation.isPending,
    refetch: query.refetch,
  };
}
